import { useEffect, useState } from "react";
import { Paper, Title, Text, Stack, Alert, Box, Group, Image as MantineImage } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconAlertTriangle } from "@nw4/ui/icons";
import styles from "./DynamicFormShell.module.css";

export const DynamicFormShell = ({ logoUrl, title, description, error, validationSummary = [], layout, children }) => {
    const [isLogoAvailable, setIsLogoAvailable] = useState(false);
    const isMobile = useMediaQuery("(max-width: 48em)");

    useEffect(() => {
        if (!logoUrl) {
            setIsLogoAvailable(false);
            return undefined;
        }

        let isCurrent = true;
        const logoImage = new window.Image();

        // Show the logo container only after the browser confirms the asset can be loaded.
        setIsLogoAvailable(false);

        logoImage.onload = () => {
            if (isCurrent) {
                setIsLogoAvailable(true);
            }
        };

        logoImage.onerror = () => {
            if (isCurrent) {
                setIsLogoAvailable(false);
            }
        };

        logoImage.src = logoUrl;

        return () => {
            isCurrent = false;
            logoImage.onload = null;
            logoImage.onerror = null;
        };
    }, [logoUrl]);

    return (
        <Stack gap="lg" align="center">
            {isLogoAvailable && (
                <Box className={styles.logoContainer}>
                    <MantineImage src={logoUrl} alt="Logo" className={styles.logo} fit="contain" />
                </Box>
            )}

            <Paper
                shadow={layout.form.withShadow ? layout.form.paperShadow : undefined}
                p={layout.form.paperPadding}
                radius={layout.form.paperRadius}
                withBorder={layout.form.withBorder}
                bg={layout.colors.paperBackground}
                w="100%"
            >
                <Stack gap="lg">
                    <Group mt="md" mb="xl" justify="space-between" align="flex-start">
                        <Box>
                            <Title order={2}>{title}</Title>
                            {description && (
                                <Text c="dimmed" mt="xs" size={isMobile ? "xs" : undefined}>
                                    {description}
                                </Text>
                            )}
                        </Box>
                    </Group>

                    {error && (
                        <Alert color="red" title="Error">
                            {error}
                        </Alert>
                    )}

                    {validationSummary.length > 0 && (
                        <Box id="form-validation-alert" className={styles.validationBox}>
                            <Group gap="xs" mb="xs" align="center">
                                <IconAlertTriangle size={20} stroke={1.5} className={styles.validationIcon} />
                                <Text fw={700} className={styles.validationTitle}>
                                    Please fix the following errors
                                </Text>
                            </Group>
                            <Text size="sm" c="dark" mb="sm">
                                Review the highlighted fields before submitting:
                            </Text>
                            <Group gap="xs" wrap="wrap">
                                {validationSummary.map((label) => (
                                    <Box key={label} className={styles.validationTag}>
                                        {label}
                                    </Box>
                                ))}
                            </Group>
                        </Box>
                    )}

                    {children}
                </Stack>
            </Paper>
        </Stack>
    );
};
